/**
 * QuoteRatesPanel.jsx - Engineers > Quote Rates: the quote builder's rate
 * card and parts catalog (Quote Master's hidden Rates, Locations and Catalog
 * sheets). Everyone with Quotes & Rates View sees it; only Edit can change
 * it. The rate card is saved whole (PUT /api/quote-rates) - a save over
 * someone else's newer one is refused and says so; catalog parts are saved
 * one at a time. Every change lands in the activity log.
 *
 * One card per entity (10/7/26): on the GLOBAL KSA tab (KsaPage's "Quote
 * rates" button) the same panel shows and saves KSA's own card - every call
 * goes to the tab's API base (useApiBase) - titled with the entity and its
 * currency ("KSA - Saudi Riyals (SAR)"). Each card says its currency and its
 * work week (the day the week starts, the always-double-time days).
 */
import { useEffect, useMemo, useState } from 'react';
import { useApiBase, useDataScope } from '../../context/DataScope';
import EditableTable from './EditableTable';
import WorkbookListsSections from './WorkbookListsSections';
import { addPart, deletePart, getRates, savePart, saveRates } from '../quotes/quoteApi';
import {
  Btn,
  Field,
  Grid,
  GREEN,
  LINE,
  MUTED,
  NumInput,
  Section,
  SelectInput,
  TextInput,
  currencyName,
  currencyOf,
  fmtMoney,
  inputStyle,
} from '../quotes/quoteUi';
import { DAY_NAMES, weekOfSettings } from '../quotes/quoteDraft';

/** The rate fields, money ones labelled in the card's currency ($ on the main card). */
const rateFields = (sym, L) => [
  ['defaultRate', `Default bill rate (${sym}/hr)`, 'Normal-time rate when the quote has no rate group'],
  ['otMult', 'Overtime multiplier', 'Hours past the weekly threshold bill at rate x this'],
  ['dtMult', 'Double-time multiplier', `Every ${L.sun || 'double-time day'} and worked-holiday hour bills at rate x this`],
  ['otThreshold', 'Overtime after (hrs / week)', `Counted from ${L.weekStart}; ${L.sun ? `${L.sun} and ` : ''}holidays never count`],
  ['expFee', 'Expense handling fee (0.085 = 8.5%)', 'Folded into each expense unit price; not on Fixed Fee POs'],
  ['legsPerTrip', 'Travel days per round trip', 'Two travel days, out and back, buy one airfare'],
  ['mileRate', `Mileage (${sym} / mile)`, ''],
  ['parkingRate', `Parking (${sym} / day)`, ''],
  ['laundryRate', `Laundry (${sym} / day)`, ''],
  ['miscRate', `Misc (${sym} / day)`, ''],
  ['costRate', `Blended internal cost (${sym}/hr)`, 'For a margin view only; never on a quote'],
];
const DEFAULT_FIELDS = [
  ['validityDays', 'Quote validity (days)'],
  ['payTerms', 'Payment terms'],
  ['invoicing', 'Invoicing'],
  ['kicker', 'Service document type'],
  ['poType', 'PO type'],
  ['siteLocation', 'Site location'],
  ['scope', 'Scope when blank'],
  ['partsSubject', 'Parts subject when blank'],
  ['partsAttention', 'Parts attention when blank'],
  ['shipVia', 'Ship via'],
  ['freightTerms', 'Freight terms'],
  ['leadNote', 'Lead time note'],
  ['rfqFallback', 'Parts PO / RFQ when blank'],
  ['warranty', 'Parts warranty'],
  ['governingLaw', 'Governing law (Clarifications) - e.g. the State of Michigan'],
];
const LISTS = [
  ['poTypes', 'PO types'],
  ['billTypes', 'Bill types'],
  ['kickers', 'Service document types'],
  ['freightTerms', 'Freight terms'],
  ['shipVia', 'Ship via'],
];
const PKG_FLAGS = ['airfare', 'hotel', 'car', 'meals', 'parking', 'laundry', 'misc'];

function CatalogTable({ catalog, setCatalog, editable, base, cur }) {
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState(catalog);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    setRows(catalog);
  }, [catalog]);
  const shown = rows
    .map((r, i) => [r, i])
    .filter(([r]) => !filter || `${r.partNo} ${r.manufacturer} ${r.description}`.toLowerCase().includes(filter.toLowerCase()));
  const set = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v, _dirty: true } : r)));
  const save = async (i) => {
    const r = rows[i];
    const res = r.id ? await savePart(r.id, r, base) : await addPart(r, base);
    if (!res.ok) {
      setMsg((res.body && res.body.error) || 'Could not save the part.');
      return;
    }
    setMsg('');
    setCatalog((c) => (r.id ? c.map((x) => (x.id === r.id ? res.body : x)) : [...c, res.body]));
  };
  const remove = async (i) => {
    const r = rows[i];
    if (!r.id) {
      setRows((rs) => rs.filter((_, j) => j !== i));
      return;
    }
    if (!window.confirm(`Remove ${r.partNo} from the catalog?`)) return;
    const res = await deletePart(r.id, base);
    if (!res.ok) {
      setMsg((res.body && res.body.error) || 'Could not remove the part.');
      return;
    }
    setCatalog((c) => c.filter((x) => x.id !== r.id));
  };
  const cell = { ...inputStyle, padding: '4px 6px', fontSize: 11.5 };
  const inp = (i, k, w, num) => (
    <input
      aria-label={`${k} ${i + 1}`}
      value={rows[i][k] ?? ''}
      readOnly={!editable}
      style={{ ...cell, width: w, textAlign: num ? 'right' : 'left', ...(editable ? null : { background: '#F7F8F5' }) }}
      onChange={(e) => set(i, k, num ? e.target.value.replace(/[^0-9.]/g, '') : e.target.value)}
    />
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <TextInput
          aria-label="Search the catalog"
          value={filter}
          onChange={setFilter}
          placeholder="Search part #, maker, description"
          style={{ maxWidth: 320 }}
        />
        {editable && (
          <Btn
            small
            onClick={() =>
              setRows((rs) => [
                ...rs,
                { partNo: '', manufacturer: '', description: '', unit: 'ea', price: 0, leadTime: '', type: 'Part', _dirty: true },
              ])
            }
          >
            + Add part
          </Btn>
        )}
        <span style={{ fontSize: 10.5, color: MUTED }}>{rows.length} parts. Price 0 = not priced yet (the quote prints TBD).</span>
      </div>
      {msg && (
        <div role="alert" style={{ color: '#B71C1C', fontSize: 11.5, fontWeight: 600, marginBottom: 6 }}>
          {msg}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table aria-label="Parts catalog" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Part #', 'Manufacturer', 'Description', 'Unit', `Sell price (${cur.symbol})`, 'Lead time', 'Type', ''].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 700,
                    color: MUTED,
                    textTransform: 'uppercase',
                    padding: '4px 6px',
                    borderBottom: `1px solid ${LINE}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map(([r, i]) => (
              <tr key={r.id || `new-${i}`}>
                <td style={{ padding: 2 }}>{inp(i, 'partNo', 150)}</td>
                <td style={{ padding: 2 }}>{inp(i, 'manufacturer', 140)}</td>
                <td style={{ padding: 2 }}>{inp(i, 'description', '100%')}</td>
                <td style={{ padding: 2 }}>{inp(i, 'unit', 50)}</td>
                <td style={{ padding: 2 }}>
                  {editable ? (
                    inp(i, 'price', 100, true)
                  ) : (
                    <span style={{ fontSize: 11.5 }}>{Number(r.price) ? fmtMoney(r.price, cur) : 'TBD'}</span>
                  )}
                </td>
                <td style={{ padding: 2 }}>{inp(i, 'leadTime', 120)}</td>
                <td style={{ padding: 2 }}>{inp(i, 'type', 90)}</td>
                <td style={{ padding: 2, whiteSpace: 'nowrap' }}>
                  {editable && r._dirty && (
                    <Btn small primary onClick={() => save(i)}>
                      Save
                    </Btn>
                  )}{' '}
                  {editable && (
                    <Btn small danger aria-label={`Remove ${r.partNo || 'new part'}`} onClick={() => remove(i)}>
                      x
                    </Btn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function QuoteRatesPanel({ editable }) {
  const base = useApiBase();
  const scope = useDataScope();
  const [state, setState] = useState({ phase: 'loading' });
  const [draft, setDraft] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [msg, setMsg] = useState(null); // { kind: 'ok'|'error', text }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setState({ phase: 'loading' });
    const r = await getRates(base);
    if (!r.ok || !r.body) {
      setState({ phase: 'failed', error: (r.body && r.body.error) || 'Could not load the quote rates.' });
      return;
    }
    setDraft(r.body.settings);
    setCatalog(r.body.catalog || []);
    setState({ phase: 'ready', saved: r.body.settings, updatedAt: r.body.updatedAt, updatedBy: r.body.updatedBy });
  };
  useEffect(() => {
    load();
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => state.phase === 'ready' && JSON.stringify(draft) !== JSON.stringify(state.saved), [draft, state]);
  if (state.phase === 'loading') return <div style={{ padding: 30, color: MUTED, fontSize: 12 }}>Loading the quote rates...</div>;
  if (state.phase === 'failed')
    return (
      <div role="alert" style={{ padding: 30, color: '#B71C1C', fontSize: 12, fontWeight: 600 }}>
        {state.error}
      </div>
    );

  const s = draft;
  const ro = !editable;
  const cur = currencyOf(s);
  const week = weekOfSettings(s);
  const L = week.labels;
  const cardName = `${scope ? 'KSA' : 'GLOBAL'} - ${currencyName(cur)}`;
  const dtDays = s.rates && Array.isArray(s.rates.dtDays) ? s.rates.dtDays.map(Number) : week.dtDays;
  const toggleDt = (d) =>
    setDraft((x) => {
      const was = x.rates && Array.isArray(x.rates.dtDays) ? x.rates.dtDays.map(Number) : week.dtDays;
      const next = was.includes(d) ? was.filter((v) => v !== d) : [...was, d];
      return { ...x, rates: { ...(x.rates || {}), dtDays: next } };
    });
  const setPart = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const setIn = (group, k) => (v) => setDraft((d) => ({ ...d, [group]: { ...(d[group] || {}), [k]: v } }));
  const save = async () => {
    setSaving(true);
    const res = await saveRates(draft, state.updatedAt, base);
    setSaving(false);
    if (res.ok) {
      setDraft(res.body.settings);
      setState({ phase: 'ready', saved: res.body.settings, updatedAt: res.body.updatedAt, updatedBy: res.body.updatedBy });
      setMsg({ kind: 'ok', text: 'Rates saved. New quotes use them from now on.' });
    } else {
      setMsg({ kind: 'error', text: (res.body && res.body.error) || 'Could not save the rates.', conflict: res.status === 409 });
    }
  };
  const names = (list) => (list || []).map((x) => x.name).filter(Boolean);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: GREEN }}>
            Quote rates{' '}
            <span
              data-testid="rate-card-name"
              style={{
                marginLeft: 6,
                fontSize: 11,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 4,
                background: '#E8F5E9',
                color: GREEN,
              }}
            >
              {cardName}
            </span>
          </div>
          <div style={{ fontSize: 11, color: MUTED }}>
            What the {scope ? 'GLOBAL KSA ' : ''}quote builder prices with, in {currencyName(cur)}. Nothing is converted.{' '}
            {state.updatedBy
              ? `Last changed by ${state.updatedBy}${state.updatedAt ? ` on ${new Date(state.updatedAt).toLocaleString()}` : ''}.`
              : ''}
            {ro ? ' You can view these; changing them needs Quotes & Rates Edit.' : ''}
          </div>
        </div>
        {editable && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {dirty && <span style={{ fontSize: 11, color: '#B07503', fontWeight: 700 }}>Unsaved changes</span>}
            <Btn
              small
              disabled={!dirty || saving}
              onClick={() => {
                setDraft(state.saved);
                setMsg(null);
              }}
            >
              Undo changes
            </Btn>
            <Btn primary disabled={!dirty || saving} onClick={save}>
              {saving ? 'Saving...' : 'Save rates'}
            </Btn>
          </span>
        )}
      </div>
      {msg && (
        <div
          role={msg.kind === 'error' ? 'alert' : 'status'}
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            background: msg.kind === 'error' ? '#FFEBEE' : '#E8F5E9',
            color: msg.kind === 'error' ? '#B71C1C' : GREEN,
          }}
        >
          {msg.text}{' '}
          {msg.conflict && (
            <Btn
              small
              onClick={() => {
                setMsg(null);
                load();
              }}
            >
              Reload the rates
            </Btn>
          )}
        </div>
      )}

      <Section
        title="Currency and work week"
        hint={`Every price on this card is in its currency. Overtime is weekly: the week starts on ${L.weekStart}; the double-time days are always double time and never count toward the overtime threshold.`}
      >
        <Grid cols={4}>
          <Field label="Currency code">
            <TextInput
              aria-label="Currency code"
              value={s.currency ? s.currency.code : 'USD'}
              disabled={ro}
              onChange={(v) => setIn('currency', 'code')(v.toUpperCase().slice(0, 3))}
            />
          </Field>
          <Field label="Currency symbol">
            <TextInput
              aria-label="Currency symbol"
              value={s.currency ? s.currency.symbol : '$'}
              disabled={ro}
              onChange={setIn('currency', 'symbol')}
            />
          </Field>
          <Field label="Currency name" span={2}>
            <TextInput
              aria-label="Currency name"
              value={s.currency ? s.currency.label : 'United States Dollars'}
              disabled={ro}
              onChange={setIn('currency', 'label')}
            />
          </Field>
          <Field label="Week starts on">
            <SelectInput
              aria-label="Week starts on"
              value={String(week.weekStart)}
              disabled={ro}
              onChange={(v) => setIn('rates', 'weekStart')(Number(v))}
              options={DAY_NAMES.map((d, i) => ({ value: String(i), label: d }))}
            />
          </Field>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, gridColumn: 'span 3', minWidth: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: 0.4, textTransform: 'uppercase' }}>
              Always double time
            </span>
            <span
              role="group"
              aria-label="Always double time"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', minHeight: 30 }}
            >
              {DAY_NAMES.map((d, i) => (
                <label
                  key={d}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 11.5,
                    color: '#1F2328',
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontWeight: 600,
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={`${d} is double time`}
                    checked={dtDays.includes(i)}
                    disabled={ro}
                    onChange={() => toggleDt(i)}
                  />
                  {d.slice(0, 3)}
                </label>
              ))}
            </span>
          </div>
        </Grid>
        <div style={{ marginTop: 8, fontSize: 10.5, color: MUTED }}>
          The calendar's hour boxes on this card: {L.wd} / {L.sat || '-'} / {L.sun || '-'}.
        </div>
      </Section>

      <Section
        title="Rates and multipliers"
        hint={`Overtime is weekly: the first hours up to the threshold, counted from ${L.weekStart}, are normal time.`}
      >
        <Grid cols={4}>
          {rateFields(cur.symbol, L).map(([k, label, hint]) => (
            <Field key={k} label={label} title={hint}>
              <NumInput aria-label={label} value={s.rates && s.rates[k]} disabled={ro} onChange={setIn('rates', k)} />
            </Field>
          ))}
        </Grid>
      </Section>

      <Section
        title="Company (document footer) and offer numbers"
        hint="The offer number is the prefix plus the opportunity's quote number."
      >
        <Grid cols={4}>
          <Field label="Company name">
            <TextInput value={s.company && s.company.name} disabled={ro} onChange={setIn('company', 'name')} />
          </Field>
          <Field label="Address" span={2}>
            <TextInput value={s.company && s.company.address} disabled={ro} onChange={setIn('company', 'address')} />
          </Field>
          <Field label="Phone">
            <TextInput value={s.company && s.company.phone} disabled={ro} onChange={setIn('company', 'phone')} />
          </Field>
          <Field label="Web">
            <TextInput value={s.company && s.company.web} disabled={ro} onChange={setIn('company', 'web')} />
          </Field>
          <Field label="Service offer prefix">
            <TextInput value={s.offerPrefixes && s.offerPrefixes.service} disabled={ro} onChange={setIn('offerPrefixes', 'service')} />
          </Field>
          <Field label="Parts offer prefix">
            <TextInput value={s.offerPrefixes && s.offerPrefixes.parts} disabled={ro} onChange={setIn('offerPrefixes', 'parts')} />
          </Field>
        </Grid>
      </Section>

      <Section title="Defaults" hint="What a new quote starts with, and what a blank field prints.">
        <Grid cols={3}>
          {DEFAULT_FIELDS.map(([k, label]) => (
            <Field key={k} label={label}>
              <TextInput value={s.defaults && s.defaults[k]} disabled={ro} onChange={setIn('defaults', k)} />
            </Field>
          ))}
        </Grid>
      </Section>

      <Section
        title="Rate groups"
        hint="One bill rate per customer group (Standard, FANUC, GM, Isuzu ...). Pick the group on a quote, or set it once per customer below."
      >
        <EditableTable
          label="Rate groups"
          readOnly={ro}
          rows={s.rateGroups || []}
          onChange={setPart('rateGroups')}
          blank={{ name: '', rate: '', note: '' }}
          columns={[
            { key: 'name', label: 'Rate group', width: 200 },
            { key: 'rate', label: `Bill rate (${cur.symbol}/hr)`, type: 'number', width: 130 },
            { key: 'note', label: 'Note' },
          ]}
        />
      </Section>

      {/* the GAT-B workbook's lists (10/12/26): the Worksheet quote's rates per level and its lists - main card only */}
      {!scope && <WorkbookListsSections s={s} setDraft={setDraft} ro={ro} cur={cur} />}

      <Section
        title="Tasks"
        hint="A team row's Expenses start as its task's package. Every Offline task defaults to None - no airfare, hotel or per diems."
      >
        <EditableTable
          label="Tasks"
          readOnly={ro}
          rows={s.tasks || []}
          onChange={setPart('tasks')}
          blank={{ name: '', defaultPackage: 'None' }}
          columns={[
            { key: 'name', label: 'Task', width: 260 },
            { key: 'defaultPackage', label: 'Default expense package', type: 'select', options: names(s.packages) },
          ]}
        />
      </Section>

      <Section title="Expense packages" hint="Ticked = that expense is added for an engineer on that package.">
        <EditableTable
          label="Expense packages"
          readOnly={ro}
          rows={s.packages || []}
          onChange={setPart('packages')}
          blank={{ name: '', ...Object.fromEntries(PKG_FLAGS.map((f) => [f, 0])), note: '' }}
          columns={[
            { key: 'name', label: 'Package', width: 180 },
            ...PKG_FLAGS.map((f) => ({ key: f, label: f, type: 'check', width: 60 })),
            { key: 'note', label: 'What it is for' },
          ]}
        />
      </Section>

      <Section title="Company holidays" hint="A holiday starts off on the calendar; working it bills double time.">
        <EditableTable
          label="Holidays"
          readOnly={ro}
          rows={s.holidays || []}
          onChange={setPart('holidays')}
          blank={{ date: '', name: '' }}
          columns={[
            { key: 'date', label: 'Date', type: 'date', width: 160 },
            { key: 'name', label: 'Holiday' },
          ]}
        />
      </Section>

      <Section
        title="Customer library"
        hint="A quote for a customer listed here starts with its address, terms, rate group and site location."
      >
        <EditableTable
          label="Customers"
          readOnly={ro}
          rows={s.customers || []}
          onChange={setPart('customers')}
          blank={{ name: '' }}
          columns={[
            { key: 'name', label: 'Customer', width: 170 },
            { key: 'addr1', label: 'Address 1' },
            { key: 'addr2', label: 'City, ST ZIP' },
            { key: 'buyer', label: 'Buyer' },
            { key: 'salutation', label: 'Salutation' },
            { key: 'payTerms', label: 'Payment terms' },
            { key: 'invoicing', label: 'Invoicing' },
            { key: 'rateGroup', label: 'Rate group', type: 'select', options: ['', ...names(s.rateGroups)] },
            { key: 'siteLocation', label: 'Site location', type: 'select', options: ['', ...names(s.locations)] },
          ]}
        />
      </Section>

      <Section
        title="Locations"
        hint={
          scope
            ? `Expense estimates per city or region, in ${cur.code}. The default site is set under Defaults.`
            : 'Expense estimates per state and province. Michigan is the default: no airfare (mileage covers it).'
        }
      >
        <EditableTable
          label="Locations"
          readOnly={ro}
          rows={s.locations || []}
          onChange={setPart('locations')}
          blank={{ name: '', region: '', hotel: 0, meals: 0, car: 0, airfare: 0, note: '' }}
          columns={[
            { key: 'name', label: 'Location', width: 170 },
            { key: 'region', label: 'Region', width: 110 },
            { key: 'hotel', label: 'Hotel / night', type: 'number', width: 100 },
            { key: 'meals', label: 'Meals / day', type: 'number', width: 100 },
            { key: 'car', label: 'Car / day', type: 'number', width: 100 },
            { key: 'airfare', label: 'Airfare round trip', type: 'number', width: 120 },
            { key: 'note', label: 'Notes' },
          ]}
        />
      </Section>

      <Section title="Pick lists" hint="One choice per line.">
        <Grid cols={5}>
          {LISTS.map(([k, label]) => (
            <Field key={k} label={label}>
              <textarea
                aria-label={label}
                rows={5}
                readOnly={ro}
                value={((s.lists && s.lists[k]) || []).join('\n')}
                onChange={(e) => {
                  const v = e.target.value.split('\n');
                  setDraft((d) => ({ ...d, lists: { ...(d.lists || {}), [k]: v } }));
                }}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </Field>
          ))}
        </Grid>
      </Section>

      <Section title="Parts catalog" hint="The Parts Quote picks part numbers from here. Each part saves on its own.">
        <CatalogTable catalog={catalog} setCatalog={setCatalog} editable={editable} base={base} cur={cur} />
      </Section>
    </div>
  );
}
